import { EDocumentType } from 'app/shared/enums/document-type.enum';
import { CustomValidator } from 'app/shared/validators/custom.validator';

export class DocumentUtils {
    /**
     * Aplica máscara baseada no tipo de documento
     * @param value Valor a ser mascarado
     * @param documentType Tipo do documento
     * @returns Valor com máscara aplicada
     */
    static applyMask(value: string, documentType: string): string {
        if (!value) return '';

        const cleanValue = value.replace(/\D/g, '');

        switch (documentType) {
            case EDocumentType.CPF:
                return this.maskCPF(cleanValue);
            case EDocumentType.CNPJ:
                return this.maskCNPJ(cleanValue);
            case EDocumentType.RG:
                return this.maskRG(cleanValue);
            default:
                return cleanValue;
        }
    }

    private static maskCPF(value: string): string {
        if (value.length <= 3) return value;
        if (value.length <= 6) return `${value.slice(0, 3)}.${value.slice(3)}`;
        if (value.length <= 9) return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6)}`;
        return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6, 9)}-${value.slice(9, 11)}`;
    }

    private static maskCNPJ(value: string): string {
        if (value.length <= 2) return value;
        if (value.length <= 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
        if (value.length <= 8) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
        if (value.length <= 12) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8)}`;
        return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12, 14)}`;
    }

    private static maskRG(value: string): string {
        if (value.length <= 2) return value;
        if (value.length <= 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
        if (value.length <= 8) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
        return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}-${value.slice(8, 9)}`;
    }

    static getMaxLength(documentType: string): number {
        switch (documentType) {
            case EDocumentType.CPF:
                return 14; // 000.000.000-00
            case EDocumentType.CNPJ:
                return 18; // 00.000.000/0000-00
            case EDocumentType.RG:
                return 12; // 00.000.000-0
            default:
                return 20;
        }
    }

    static getPlaceholder(documentType: string): string {
        switch (documentType) {
            case EDocumentType.CPF:
                return '000.000.000-00';
            case EDocumentType.CNPJ:
                return '00.000.000/0000-00';
            case EDocumentType.RG:
                return '00.000.000-0';
            default:
                return '';
        }
    }

    static validateDocument(value: string, documentType: string): boolean {
        if (!value) return false;

        const cleanValue = value.replace(/\D/g, '');

        switch (documentType) {
            case EDocumentType.CPF:
                return this.validateCPF(cleanValue);
            case EDocumentType.CNPJ:
                return this.validateCNPJ(cleanValue);
            case EDocumentType.RG:
                return this.validateRG(cleanValue);
            default:
                return true;
        }
    }

    private static validateCPF(cpf: string): boolean {
        if (cpf.length !== 11) return false;

        const mockControl = { value: cpf };
        return !CustomValidator.validateCPF_CNPJ(mockControl as any);
    }

    private static validateCNPJ(cnpj: string): boolean {
        if (cnpj.length !== 14) return false;

        const mockControl = { value: cnpj };
        return !CustomValidator.validateCPF_CNPJ(mockControl as any);
    }

    private static validateRG(rg: string): boolean {
        return rg.length >= 7 && rg.length <= 10;
    }

    static getErrorMessage(documentType: string): string {
        switch (documentType) {
            case EDocumentType.CPF:
                return 'CPF inválido';
            case EDocumentType.CNPJ:
                return 'CNPJ inválido';
            case EDocumentType.RG:
                return 'RG inválido';
            default:
                return 'Documento inválido';
        }
    }

    static getMask(documentType: EDocumentType): string {
        switch (documentType) {
            case EDocumentType.CPF:
                return '000.000.000-00';
            case EDocumentType.CNPJ:
                return '00.000.000/0000-00';
            case EDocumentType.RG:
                return '00.000.000-0';
        }
        return '';
    }
}
